import useUnauthorize from '@/hooks/useUnauthorize';
import { CustomDeleteModal } from '@/pages/Modals';
import { DeleteIcon } from '@/assets/icons/icons';
import { Button, Divider, Flex, message, Tag, Tooltip, Upload } from 'antd';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'tippy.js/dist/tippy.css';
import {
  ChainOfCustodyComments,
  checkMentionUsers,
  ReplyComment,
} from '../../../apis/shipment';
import {
  AttachFileIcon,
  MentionIcon,
  URLIcon,
} from '../../../assets/icons/icons';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import AddLinkForm from './AddLinkForm';
import AudienceSelector from './COC_Privacy_Comments/AudienceSelector';
import RichEditor, { RichEditorRef } from './RichEditor';
import { ControlTowerView } from '../ControlTower';
import { typeMapping } from '@/common/static_data';
import { useQueryClient } from '@tanstack/react-query';
import { UPDATES_INBOX_QUERY_KEY } from '@/pages/Notifications/hooks/useUpdatesInbox';

export interface ShipmentTeam {
  id: string;
  name: string;
  color?: string;
  company: string;
  created_at: string;
  created_by: string;
  general_support_email: string;
  is_archived: boolean;
  slack_webhook: string;
  teams_webhook: string;
  updated_at: string;
  updated_by: string;
}
export interface AllCommonTeams {
  id: string;
  name: string;
  color?: string;
}

interface MentionRecipient {
  id: string | number;
  name: string;
  email?: string;
  author_badge_color?: string;
}

export interface Custody {
  id: number | string;
  type?: number;
  audience_type?: string;
}

interface LastMessage {
  id?: number | string;
}
export interface AudienceMeta {
  audience: 'public' | 'my_team' | 'specific_teams';
  teamIds?: (number | string)[];
}
export interface AudienceMetaID {
  id?: (number | string)[];
}

export interface CocCommentCreatedMeta {
  text: string;
  authorName?: string | null;
  timestamp: string;
}

interface MessageText {
  message?: string;
  team_names?: string;
  team_id?: string | number;
  muted_user_ids?: string[];
}

interface MentionUser {
  id: string | number;
  name: string;
  email?: string;
  author_badge_color?: string;
}

interface CheckMentionUsersData {
  team_id?: string | number;
  team_names?: string;
  message?: string;
}

interface CheckMentionUsersResponse {
  status_code?: 200 | 403 | number;
  data?: CheckMentionUsersData | MentionUser[];
  is_success?: boolean;
  message?: string;
}

interface ReplyCommentBoxProps {
  custody?: Custody;
  mentionRecipients?: MentionRecipient[];
  setOpenReplyCommentInput: (value: null) => void;
  fetchSingleShipmentData: (showSpinner: boolean) => void;
  lastMessage?: LastMessage;
  shipmentId?: string;
  isParentComment?: boolean;
  specificTeams?: AllCommonTeams[]; // <--- array
  myTeams?: ShipmentTeam[]; // <--- array
  onSearchMentions?: (query: string) => Promise<MentionRecipient[]>;
  onCommentCreated?: (
    shipmentId: string,
    comment: CocCommentCreatedMeta
  ) => void;
}

/**
 * Component for posting comments and replies with rich text editor support
 *
 * @param custody - Chain of custody or alert reference data
 * @param mentionRecipients - List of users available for mentions
 * @param setOpenReplyCommentInput - Callback to close the comment input
 * @param fetchSingleShipmentData - Callback to refresh shipment data
 * @param lastMessage - Parent message for threading replies
 * @param shipmentId - ID of the shipment for parent comments
 * @param isParentComment - Whether this is a parent comment or reply
 */
const ReplyCommentBox: React.FC<ReplyCommentBoxProps> = ({
  custody,
  mentionRecipients,
  setOpenReplyCommentInput,
  fetchSingleShipmentData,
  lastMessage,
  shipmentId,
  myTeams = [],
  specificTeams = [],
  isParentComment = false,
  onSearchMentions,
  onCommentCreated,
}) => {
  const { showSpin, hideSpin } = useSpin();

  const { token, decodedUserToken } = useToken();
  const { unauthorize } = useUnauthorize();
  const queryClient = useQueryClient();

  const [showAddTeamModal, setShowAddTeamModal] = useState(false);

  const [messageText, setMessageText] = useState<MessageText>({});
  // Files state
  const [selectedFiles, setSelectedFiles] = useState<UploadFile[]>([]);

  const selectedTeamIDsRef = useRef<string[] | number[]>([]);

  const [selectedTeamIDs, setSelectedTeamIDs] = useState<string[]>(
    myTeams?.map((team) => team.id) || []
  );

  useEffect(() => {
    selectedTeamIDsRef.current = selectedTeamIDs;
  }, [selectedTeamIDs]);

  const handleFileChange = (fileList: UploadChangeParam) => {
    const newFile = fileList?.file;
    if (newFile) setSelectedFiles((prv) => [...prv, newFile]);
  };

  const handleRemoveFile = (uidToRemove: string) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((f) => f?.uid !== uidToRemove)
    );
  };

  // Mentions state → IDs only (integers or UUID strings)
  const [mentionedUsers, setMentionedUsers] = useState<(string | number)[]>([]);

  // const [mutedMentions, setMutedMentions] = useState<number[]>([]);
  const [audienceType, setAudienceType] = useState<AudienceMeta | null>({
    audience: 'my_team',
    teamIds: myTeams?.map((t: ShipmentTeam) => t.id),
  });

  const handleOnChangeAudience = (meta: AudienceMeta) => {
    setAudienceType(meta);
    if (meta?.teamIds) {
      setSelectedTeamIDs(meta.teamIds.map((id) => String(id)));
    } else {
      setSelectedTeamIDs([]);
    }
  };

  const handleSkipMention = () => {
    setShowAddTeamModal(false);
    setLoading(false);
    handlePostComment(selectedTeamIDs, false);
  };

  // Link modal
  const [visible, setVisible] = useState<boolean>(false);
  const [linkText, setLinkText] = useState<string>('');

  // Editor ref
  const editorRef = useRef<RichEditorRef>(null);

  // Build color map
  const userColorMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    mentionRecipients?.forEach((resp) => {
      const user = resp?.name;

      if (user && !map[user]) {
        map[user] = resp?.author_badge_color || '#1677ff';
      }
    });
    return map;
  }, [mentionRecipients]);
  // Mention icon → insert "@" at cursor
  const handleMentionIconClick = () => {
    editorRef.current?.insertAtSymbol();
  };

  // Link icon → open modal with selected text prefilled
  const openModal = () => {
    const selected = editorRef.current?.getSelectedText?.() || '';
    setLinkText(selected);
    setVisible(true);
  };

  function addMutedClassToMentions(html: string, mutedUserIds: string[] = []) {
    // Create a DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Find all mention nodes
    const mentionNodes = doc.querySelectorAll('.mention-node[user-id]');
    mentionNodes.forEach((node) => {
      const userId = node.getAttribute('user-id');

      if (userId && mutedUserIds.includes(userId)) {
        node.classList.add('mention-node--muted');
      }
    });
    return doc.body.innerHTML;
  }

  // Submit
  const handlePostComment = (
    teamIdsParam?: Array<string | number>,
    addUserToTeams: boolean = false
  ) => {
    const html = editorRef.current?.getHTML() || '';
    // const mutedUserIDs = ['627', '746', '795'];
    const htmlComment =
      (messageText?.muted_user_ids?.length ?? 0) > 0 && !addUserToTeams
        ? addMutedClassToMentions(html, messageText?.muted_user_ids)
        : html;

    const teamIds = teamIdsParam ?? selectedTeamIDs;

    if (isParentComment) {
      if (!shipmentId) {
        message.error('Shipment ID is missing!');
        return;
      }
    } else {
      if (!custody?.id) {
        message.error('Comment ID is missing!');
        return;
      }
    }
    const formData = new FormData();

    formData.append(isParentComment ? 'comment' : 'content', htmlComment);

    // Audience case
    formData.append('audience_type', audienceType?.audience ?? '');

    if (teamIds?.length > 0 && audienceType?.audience !== 'public') {
      teamIds.forEach((id) => formData.append('audience_teams', String(id)));
    }

    if (mentionedUsers?.length > 0) {
      // Backend expects array or stringified? Using array directly first
      mentionedUsers.forEach((id) =>
        formData.append('mentioned_users', String(id))
      );
    }

    selectedFiles.forEach((file) => {
      // @ts-expect-error - TODO(tim) fix typing
      formData.append('attachments', file);
    });

    if (isParentComment) {
      formData.append('shipment', shipmentId ?? '');
      formData.append('content_type', 'rich_text');
    } else {
      formData.append('reference_id', String(custody?.id));
      if (custody?.type && custody.type in typeMapping) {
        formData.append('reference_type', typeMapping[custody.type]);
      }
      formData.append(
        'parent_message',
        lastMessage?.id ? String(lastMessage?.id) : ''
      );
    }

    showSpin();
    const commentAPICall = isParentComment
      ? ChainOfCustodyComments
      : ReplyComment;

    commentAPICall(token ?? '', formData)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          const commentMeta: CocCommentCreatedMeta = {
            text: htmlComment,
            authorName: decodedUserToken()?.name,
            timestamp: new Date().toISOString(),
          };

          setOpenReplyCommentInput(null);
          fetchSingleShipmentData(false);
          message.success(data?.message);
          const _ = queryClient.invalidateQueries({
            queryKey: UPDATES_INBOX_QUERY_KEY,
          });
          if (shipmentId) {
            onCommentCreated?.(shipmentId, commentMeta);
          }
        } else {
          message.error(data?.message);
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
        setLoading(false);
      });
  };

  const [loading, setLoading] = useState<boolean>(false);

  const buildQueryParams = (
    shipmentId: string,
    mentionedUsers: (string | number)[],
    audienceType: AudienceMeta | null
  ): string => {
    const userIdsParam = mentionedUsers?.length
      ? `user_ids=${encodeURIComponent(mentionedUsers?.map(String).join(','))}`
      : '';

    const teamIdsParam = audienceType?.teamIds?.length
      ? `team_ids=${encodeURIComponent(
          audienceType?.teamIds?.map(String).join(',')
        )}`
      : '';
    const shipmentParam = `shipment_id=${encodeURIComponent(shipmentId)}`;
    const query = [userIdsParam, teamIdsParam, shipmentParam]
      .filter(Boolean)
      .join('&');

    return `?${query}`;
  };

  const handleCheckMentionsUsers = () => {
    if (audienceType?.audience === 'public' || mentionedUsers?.length < 1) {
      handlePostComment(selectedTeamIDs, false);
      return;
    }
    setLoading(true);
    const queryString = buildQueryParams(
      shipmentId ?? '',
      mentionedUsers,
      audienceType
    );
    checkMentionUsers(token ?? '', queryString)
      .then((res) => {
        const additionalOptions: CheckMentionUsersResponse | undefined =
          res?.data?.data;
        if (
          additionalOptions?.status_code === 403 &&
          !Array.isArray(additionalOptions.data)
        ) {
          setMessageText(additionalOptions.data ?? {});
          setShowAddTeamModal(true);
        }

        if (additionalOptions?.status_code === 200) {
          handlePostComment(selectedTeamIDs, false);
        }
      })
      .catch((error) => {
        unauthorize(error);
      });
  };

  const handleAddAccess = () => {
    setSelectedTeamIDs((prev) => {
      const updated = [...prev, messageText?.team_id].filter(
        Boolean
      ) as string[];
      handlePostComment(updated, true);
      return updated;
    });
  };

  const [isEmpty, setIsEmpty] = useState<boolean>(true);

  const envView = process.env.REACT_APP_CONTROL_TOWER_VIEW as ControlTowerView;
  const isTableView = envView !== 'list';

  return (
    <div className='mt-3 mb-3 relative'>
      {/* Editor */}
      <RichEditor
        ref={editorRef}
        mentionRecipients={mentionRecipients}
        userColorMap={userColorMap}
        onEmptyChange={setIsEmpty}
        placeholder={isParentComment ? 'Write a comment...' : 'Add reply...'}
        currentAudienceType={1}
        setMentionedUsers={setMentionedUsers}
        isTableView={isTableView}
        onSearchMentions={onSearchMentions}
      />

      {/* Actions bar (unchanged layout) */}
      <div
        className={`comment-actions ${isTableView ? 'responsive-comment-actions' : ''}`}
      >
        <Flex align='center'>
          <AudienceSelector
            myTeams={myTeams}
            specificTeams={specificTeams}
            primaryTeamName={
              myTeams?.length === 1 ? myTeams[0]?.name : undefined
            }
            defaultAudience={
              custody?.audience_type ? custody?.audience_type : 'my_team'
            }
            hideIfSingleTeam={false}
            onChange={(meta) => handleOnChangeAudience(meta)}
            setSelectedTeamID={setSelectedTeamIDs}
            custody={custody}
          />
          <Divider type='vertical' style={{ height: '20px' }} />
          <div className='left-icons'>
            <Upload
              beforeUpload={() => false}
              showUploadList={false}
              multiple={false}
              onChange={handleFileChange}
            >
              <AttachFileIcon />
            </Upload>

            <span onClick={handleMentionIconClick}>
              <MentionIcon />
            </span>

            <span
              onClick={(e) => {
                e.preventDefault();
                openModal();
              }}
            >
              <URLIcon />
            </span>
          </div>
        </Flex>

        <div className='right-buttons'>
          <Button
            className='cancel-btn'
            type='text'
            onClick={() => setOpenReplyCommentInput(null)}
          >
            Cancel
          </Button>
          <Button
            disabled={
              loading ||
              (isEmpty && (!selectedFiles || selectedFiles.length === 0))
            }
            onClick={handleCheckMentionsUsers}
            type='primary'
            className='cancel-btn submit-btn'
          >
            Submit
          </Button>
        </div>
      </div>

      {/* File chips */}
      {selectedFiles?.map((file) => (
        <Tag key={file.uid} className='file-tag mt-2'>
          <Tooltip title={file?.name}>
            {file?.name?.length > 15
              ? file?.name.slice(0, 15) + '...'
              : file?.name}
          </Tooltip>
          <DeleteIcon
            color='currentColor'
            role='img'
            aria-label='delete'
            onClick={() => handleRemoveFile(file.uid)}
          />
        </Tag>
      ))}

      {/* Link Modal */}
      <AddLinkForm
        editorRef={editorRef}
        setVisible={setVisible}
        visible={visible}
        setLinkText={setLinkText}
        linkText={linkText}
      />

      <CustomDeleteModal
        titleFontSize={20}
        messageFontSize={14}
        visible={showAddTeamModal}
        width={600}
        onClose={() => {
          setShowAddTeamModal(false);
          setLoading(false);
        }} // on close
        onConfirm={() => handleAddAccess()} // on apply action
        addButton={
          <Button
            type='text'
            onClick={() => handleSkipMention()}
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              height: '40px',
            }}
          >
            Skip
          </Button>
        }
        title={`Add Team`}
        message={messageText?.message}
        // cancelText='Skip'
        confirmText={`Add Team ${messageText?.team_names}`}
        confirmColor={`#000`}
        titleMargin='16px 0 0px 0'
        buttonHeight={36}
        closeIconSize={10}
      />
    </div>
  );
};

export default ReplyCommentBox;
